/**
 * Schaff Trend Cycle (STC) Calculator
 *
 * The Schaff Trend Cycle is a double-smoothed stochastic of MACD that oscillates between 0 and 100.
 * It provides early signals of trend changes by combining cycle and momentum concepts.
 *
 * Algorithm (per ChartIQ lines 2282-2326):
 * 1. Calculate MACD: MA(shortCycle) - MA(longCycle) using specified MA type
 * 2. Apply first stochastic oscillator to MACD with per-bar smoothing (factor 0.5) → PF (Percent F)
 * 3. Apply second stochastic oscillator to PF with per-bar smoothing (factor 0.5) → STC
 *
 * Key difference from standard stochastic: Uses per-bar exponential smoothing
 * instead of calculating stochastic on entire array then smoothing.
 *
 * Range: 0-100
 * Overbought: > 75
 * Oversold: < 25
 *
 * Reference: ChartIQ calculateSchaff (calcsChartIQ.js lines 2282-2326)
 */

import { isNotDefined } from "../utils";
import getMACalculator from "../utils/getMACalculator";

const defaultOptions = {
  period: 10, // Stochastic period
  shortCycle: 23, // MACD short EMA period
  longCycle: 50, // MACD long EMA period
  sourcePath: "close", // Source field
  movingAverageType: "ema", // Moving average type
};

/**
 * Finds highest and lowest values in a window
 * Matches ChartIQ's getLLVHHV function (lines 2298-2307)
 */
function getHighLow(series, endIndex, period) {
  let low = null;
  let high = null;

  for (let j = endIndex - period + 1; j <= endIndex; j++) {
    const value = series[j];
    if (isNotDefined(value)) continue;

    low = low === null ? value : Math.min(low, value);
    high = high === null ? value : Math.max(high, value);
  }

  return [low, high];
}

export default function schaffTrendCycle() {
  let options = defaultOptions;

  function calculator(data) {
    const { period, shortCycle, longCycle, sourcePath, movingAverageType } =
      options;

    // Get the appropriate MA calculator using centralized utility
    const maCalculator = getMACalculator(movingAverageType || "ema");

    // Step 1: Calculate short and long MAs (lines 2295-2296)
    const shortMA = maCalculator().options({
      windowSize: shortCycle,
      sourcePath: sourcePath,
    })(data);

    const longMA = maCalculator().options({
      windowSize: longCycle,
      sourcePath: sourcePath,
    })(data);

    // Step 2: Calculate MACD series (line 2314)
    const macdSeries = data.map((d, i) => {
      if (isNotDefined(shortMA[i]) || isNotDefined(longMA[i])) {
        return undefined;
      }
      return shortMA[i] - longMA[i];
    });

    // Step 3: First stochastic with per-bar smoothing → PF (lines 2317-2319)
    const pf = [];
    let f1 = 0;

    for (let i = 0; i < data.length; i++) {
      // Need enough data for longCycle + (period - 1)
      if (i < longCycle + period - 1) {
        pf.push(undefined);
        continue;
      }

      const macd = macdSeries[i];
      if (isNotDefined(macd)) {
        pf.push(undefined);
        continue;
      }

      // Get high/low of MACD in window
      const [low, high] = getHighLow(macdSeries, i, period);

      // Calculate raw stochastic
      if (high > low) {
        f1 = (100 * (macd - low)) / (high - low);
      }
      // else keep previous f1 (avoid division by zero)

      // Apply exponential smoothing (factor = 0.5)
      const previousPF = pf[i - 1];
      const currentPF = isNotDefined(previousPF)
        ? f1
        : previousPF + 0.5 * (f1 - previousPF);

      pf.push(currentPF);
    }

    // Step 4: Second stochastic with per-bar smoothing → STC (lines 2322-2324)
    const stc = [];
    let f2 = 0;

    for (let i = 0; i < data.length; i++) {
      // Need enough data for longCycle + 2 * (period - 1)
      if (i < longCycle + 2 * (period - 1)) {
        stc.push(undefined);
        continue;
      }

      const currentPF = pf[i];
      if (isNotDefined(currentPF)) {
        stc.push(undefined);
        continue;
      }

      // Get high/low of PF in window
      const [low, high] = getHighLow(pf, i, period);

      // Calculate raw stochastic
      if (high > low) {
        f2 = (100 * (currentPF - low)) / (high - low);
      }
      // else keep previous f2 (avoid division by zero)

      // Apply exponential smoothing (factor = 0.5)
      const previousSTC = stc[i - 1];
      const currentSTC = isNotDefined(previousSTC)
        ? f2
        : previousSTC + 0.5 * (f2 - previousSTC);

      stc.push(currentSTC);
    }

    return stc;
  }

  /**
   * Returns the number of undefined values at the start
   */
  calculator.undefinedLength = function () {
    const { period, longCycle } = options;
    // Need enough data for long EMA + 2 stochastic periods
    return longCycle + (period - 1) * 2;
  };

  /**
   * Gets or sets calculator options
   */
  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
